import React, {useEffect, useMemo, useRef, useState} from 'react';
import './CoursesFilter.css';
import MultiselectBadgesBlock from './MultiselectBadgesBlock';
import LevelSelect from '../../components/LevelSelect';
import CheckBoxesBlock from '../../components/CheckBoxesBlock';

export type CoursesFilterState = {
  // 0 = Beginner, 1 = Intermediate, 2 = Advanced
  difficulty?: 0 | 1 | 2;
  // sort options for courses list
  sort?: 'popular' | 'newest' | 'rating' | 'shortest';
  // multiselect categories by name/slug
  categories?: string[];
  // additional multiselect tags
  tags?: string[];
};

export type CoursesFilterProps = {
  open: boolean;
  initialState?: CoursesFilterState;
  onApply: (state: CoursesFilterState) => void;
  onClose: () => void;
};

/**
 * CoursesFilter
 * A lightweight popup modal for filtering courses, placed in RecommendedCourses folder.
 * It focuses on the available filtering in code (completed-only) and can be extended later.
 */
const CoursesFilter: React.FC<CoursesFilterProps> = ({ open, initialState, onApply, onClose }) => {
  const CATEGORY_OPTIONS = useMemo(() => [
    'Midjourney',
    'ChatGPT',
    'Automation',
    'Prompting',
    'Business Tools',
    'Side Hustles'
  ], []);

  const TAG_OPTIONS = useMemo(() => [
    'AI',
    'Marketing',
    'Design',
    'Productivity',
    'Coding',
    'No-code'
  ], []);

  const [state, setState] = useState<CoursesFilterState>({
    difficulty: initialState?.difficulty ?? 1,
    sort: initialState?.sort ?? 'popular',
    categories: initialState?.categories ?? [],
    tags: initialState?.tags ?? [],
  });

  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Sync local state when initial changes
  useEffect(() => {
    setState({
      difficulty: initialState?.difficulty ?? 1,
      sort: initialState?.sort ?? 'popular',
      categories: initialState?.categories ?? [],
      tags: initialState?.tags ?? [],
    });
  }, [initialState?.difficulty, initialState?.sort, initialState?.categories, initialState?.tags]);

  // Close on escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!dialogRef.current) return;
      if (e.target instanceof Node && !dialogRef.current.contains(e.target)) {
        onClose();
      }
    };
    const overlay = overlayRef.current;
    overlay?.addEventListener('mousedown', onClick);
    return () => overlay?.removeEventListener('mousedown', onClick);
  }, [open, onClose]);

  // Keep mounted shortly after close to allow exit animation
  const [shouldRender, setShouldRender] = useState(open);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      setClosing(false);
    } else if (shouldRender) {
      // trigger closing state to play animation
      setClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setClosing(false);
      }, 260); // match CSS animation duration
      return () => clearTimeout(timer);
    }
  }, [open]);//eslint-disable-line react-hooks/exhaustive-deps

  if (!shouldRender) return null;

  return (
    <div className={"cf-overlay" + (closing ? " cf-overlay--closing" : "")} ref={overlayRef} role="dialog" aria-modal="true" aria-labelledby="cf-title">
      <div className={"cf-dialog" + (closing ? " cf-dialog--closing" : "")} ref={dialogRef}>
        <div className="cf-header">
          {/*<h2 id="cf-title" className="cf-title">Filters</h2>*/}
          <button className="cf-close" aria-label="Close" onClick={onClose} type="button">✕</button>
        </div>
        <div className="cf-content">
          {/* Difficulty level block */}
          <LevelSelect
            value={state.difficulty ?? 1}
            onChange={(v) => setState(s => ({ ...s, difficulty: v }))}
          />
          {/* Category multiselect block */}
          <MultiselectBadgesBlock
            id="cf-cat-title"
            title="Category"
            options={CATEGORY_OPTIONS}
            selected={state.categories ?? []}
            onToggle={(cat) =>
              setState(s => {
                const cur = s.categories ?? [];
                return {
                  ...s,
                  categories: cur.includes(cat)
                    ? cur.filter(c => c !== cat)
                    : [...cur, cat],
                };
              })
            }
          />

          {/* Tags multiselect block */}
          <MultiselectBadgesBlock
            id="cf-tags-title"
            title="Tags"
            options={TAG_OPTIONS}
            selected={state.tags ?? []}
            onToggle={(tag) =>
              setState(s => {
                const cur = s.tags ?? [];
                return {
                  ...s,
                  tags: cur.includes(tag)
                    ? cur.filter(t => t !== tag)
                    : [...cur, tag],
                };
              })
            }
          />

          {/* Sort block */}
          <CheckBoxesBlock
            id="cf-sort-title"
            title="Sort"
            type="radio"
            name="cf-sort"
            options={[
              { value: 'popular', label: 'Most popular' },
              { value: 'newest', label: 'Newest' },
              { value: 'rating', label: 'Highest Rated' },
              { value: 'shortest', label: 'Shortest Duration' },
            ]}
            value={state.sort ?? 'popular'}
            onChange={(val) => setState(s => ({ ...s, sort: val as CoursesFilterState['sort'] }))}
          />
        </div>
        <div className="cf-footer">
          <button className="cf-btn cf-btn-secondary" type="button" onClick={() => setState({ difficulty: 1, sort: 'popular', categories: [], tags: [] })}>
            Clear
          </button>
          <button className="cf-btn cf-btn-primary" type="button" onClick={() => onApply(state)}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default CoursesFilter;
